import React from 'react';

import { convertEnergyPricePowertracerToPartner } from 'src/util/conversions';

/**
 *  Returns the unit of measure, as an abbr wrapped element, of the unit price of energy using
 * the currency internationlization.
 * @param {import('react-intl').IntlShape} intlCurrency - The currency internationalization
 *   object for formatting unit messages.
 * @returns {React.ReactElement|string|null} The formatted unit element
 */
export const unitOfMeasure = (intlCurrency) => {
  if (!intlCurrency) {
    return null;
  }
  const unitTitle = intlCurrency.formatMessage({ id: 'util.i18n.energy_price.unit_of_measure.abbr.title', defaultMessage: 'cents per kilowatt hour' });
  const unitLabel = intlCurrency.formatMessage({ id: 'util.i18n.energy_price.unit_of_measure.abbr.label', defaultMessage: 'c/kWh' });

  return <abbr title={unitTitle}>{unitLabel}</abbr>;
};

/**
 * Gets a formatted display value for energy pricing with currency conversion and localization.
 * @param {import('react-intl').IntlShape} intlCurrency - The currency internationalization object
 *   used for currency-specific price conversions in convertEnergyPricePowertracerToPartner
 * @param {number|null|undefined} value - The energy price value in Powertracer format
 *   (dollars per Wh) to be converted and formatted. Can be null, undefined, or any numeric value
 *   including 0
 * @returns {string|null} The formatted price string with 3 decimal places in partner format
 *   (cents per kWh), 'N/A' when the value is invalid, or null when intlCurrency is missing
 * @example
 * // Format a valid energy price (0.00015 dollars/Wh -> 15.000 cents/kWh)
 * const formattedPrice = getDisplayValue(intl, intlCurrency, 0.00015);
 * // Returns: "15.000" (formatted according to locale)
 * @example
 * // Handle invalid values
 * const invalidPrice = getDisplayValue(intl, intlCurrency, null);
 * // Returns: "-"
 * @example
 * // Handle zero values (valid case)
 * const zeroPrice = getDisplayValue(intl, intlCurrency, 0);
 * // Returns: "0.000" (formatted according to locale)
 */
export const displayValue = (intlCurrency, value) => {
  if (!intlCurrency) {
    return null;
  }

  const convertedValue = convertEnergyPricePowertracerToPartner(intlCurrency, value);

  if (convertedValue === null) {
    return intlCurrency.formatMessage(
      { id: 'util.i18n.energy_price.display_value.invalid.label', defaultMessage: 'N/A' },
    );
  }

  return intlCurrency.formatMessage(
    { id: 'util.i18n.energy_price.display_value.valid.label', defaultMessage: '{n, number, :: .000#}' },
    { n: convertedValue },
  );
};

/**
 * Builds a formatted value display for a time-of-usable entity with currency conversion
 * and internationalisation.
 *
 * This function converts energy prices/rates from the internal format to partner format,
 * applies locale-specific decimal formatting, and creates a React element with
 * the formatted price and unit abbreviation.
 * @param {import('react-intl').IntlShape} intl - The internationalisation object for
 * formatting messages
 * @param {import('react-intl').IntlShape} intlCurrency - The currency internationalization object
 * for price formatting and message translation
 * @param {number} value - The value to be converted and formatted representing a price/rate of
 * energy.
 * @returns {React.ReactNode[]|null} i18n formatted content containing the price and unit with JSX
 * elements
 */
export const valuePerUnit = (intl, intlCurrency, value) => {
  if (!intl || !intlCurrency) {
    return null;
  }

  return intl.formatMessage({
    id: 'util.i18n.energy_price.value_per_unit.text',
    defaultMessage: '{formattedValue} {unitElement}',
  }, {
    formattedValue: displayValue(intlCurrency, value),
    unitElement: unitOfMeasure(intlCurrency),
  });
};

/**
 * Builds a formatted value display for a time-of-usable entity with currency conversion
 * and internationalisation.
 *
 * This function converts energy prices/rates from the internal format to partner format,
 * applies locale-specific decimal formatting, and creates a React element with
 * the formatted price and unit abbreviation.
 * @param {import('react-intl').IntlShape} intl - The internationalisation object for
 * formatting messages
 * @param {import('react-intl').IntlShape} intlCurrency - The currency internationalization object
 * for price formatting and message translation
 * @param {number} value - The value to be converted and formatted representing a price/rate of
 * energy.
 * @returns {React.ReactNode[]|null} i18n formatted content containing the price and unit with JSX
 * elements
 */
export const valuePerUnitWithStrong = (intl, intlCurrency, value) => {
  if (!intl || !intlCurrency) {
    return null;
  }

  return intl.formatMessage({
    id: 'util.i18n.energy_price.value_per_unit.text',
    defaultMessage: '{formattedValue} {unitElement}',
  }, {
    formattedValue: <strong>{displayValue(intlCurrency, value)}</strong>,
    unitElement: unitOfMeasure(intlCurrency),
  });
};

/**
 * Builds a formatted energy price range display for time-of-use pricing.
 *
 * This function creates a localized display of energy price ranges used in time-of-use tariff
 * structures, where different time periods have different pricing tiers.
 * @param {import('react-intl').IntlShape} intl - The internationalization object
 * @param {import('react-intl').IntlShape} intlCurrency - The currency-specific internationalization
 *   object
 * @param {object} range - The price range object containing the energy pricing boundaries for
 *   a time-of-use period.
 * @param {number} range.minimum - The minimum energy price value in dollars per Wh
 * @param {number} range.maximum - The maximum energy price value in dollars per Wh
 * @returns {React.ReactNode|null} A formatted internationalized message containing the price
 *   range with minimum and maximum values, followed by the appropriate unit abbreviation
 *   (e.g., "15.000 to 25.000 c/kWh"). Returns null if either intl parameter is invalid.
 */
export const valueRangePerUnit = (intl, intlCurrency, range) => {
  if (range.maximum === range.minimum) {
    return valuePerUnit(intl, intlCurrency, range.minimum);
  }

  const minValue = displayValue(intlCurrency, range.minimum);
  const maxValue = displayValue(intlCurrency, range.maximum);
  const valueUnit = unitOfMeasure(intlCurrency);

  return intl.formatMessage(
    {
      id: 'util.i18n.energy_price.value_range_per_unit.text',
      defaultMessage: '{minValue} to {maxValue} {valueUnit}',
    },
    { minValue, maxValue, valueUnit },
  );
};

/**
 * Builds a formatted energy price range display with emphasized values for time-of-use pricing.
 *
 * This function creates a localized display of energy price ranges used in time-of-use tariff
 * structures, where different time periods have different pricing tiers.
 * @param {import('react-intl').IntlShape} intl - The internationalization object
 * @param {import('react-intl').IntlShape} intlCurrency - The currency-specific internationalization
 *   object
 * @param {object} range - The price range object containing the energy pricing boundaries for
 *   a time-of-use period.
 * @param {number} range.minimum - The minimum energy price value in dollars per Wh
 * @param {number} range.maximum - The maximum energy price value in dollars per Wh
 * @returns {React.ReactNode|null} A formatted internationalized message containing the price
 *   range with both minimum and maximum values wrapped in <strong> elements for emphasis,
 *   followed by the appropriate unit abbreviation (e.g., "15.000 to 25.000 c/kWh"). Returns
 *   null if either intl parameter is invalid. When minimum equals maximum, returns a single
 *   emphasized value using valuePerUnitWithStrong instead of a range display.
 */
export const valueRangePerUnitWithStrong = (intl, intlCurrency, range) => {
  if (range.maximum === range.minimum) {
    return valuePerUnitWithStrong(intl, intlCurrency, range.minimum);
  }

  const minValue = displayValue(intlCurrency, range.minimum);

  const minValueElement = (
    <strong>
      {minValue}
    </strong>
  );

  const maxValue = displayValue(intlCurrency, range.maximum);

  const maxValueElement = (
    <strong>
      {maxValue}
    </strong>
  );

  const valueUnit = unitOfMeasure(intlCurrency);

  return intl.formatMessage(
    {
      id: 'util.i18n.energy_price.value_range_per_unit.text',
      defaultMessage: '{minValueElement} to {maxValueElement} {valueUnit}',
    },
    { minValueElement, maxValueElement, valueUnit },
  );
};
